import { Airline } from "@/types/flights/airline"
import {
  FlightDateOutOfRangeException,
  FlightExpirationException,
  FlightOfferUnavailableException,
  FlightPassengersChangedException,
} from "@/types/flights/exceptions"
import {
  FlightBooking,
  FlightBookingStatus,
  PRE_SUBMIT_STATUSES,
} from "@/types/flights/flight-booking"
import { FlightContractUpgrade } from "@/types/flights/flight-contract-upgrade"
import { FlightItineraryApi } from "@/types/flights/flight-itinerary-api"
import { FlightLegApi } from "@/types/flights/flight-leg"
import {
  FlightOption,
  FlightOptionPayload,
} from "@/types/flights/flight-option"
import { FlightSearchFilters } from "@/types/flights/flight-search-filters"
import { FlightSearchInput } from "@/types/flights/flight-search-input"
import {
  FlightLocationIdentifier,
  FlightSearchOptions,
  FlightTravelers,
  MultiCitySegment,
} from "@/types/flights/flight-search-options"
import { FlightSearchParams } from "@/types/flights/flight-search-params"
import { FlightSearchResult } from "@/types/flights/flight-search-result"
import { FlightSearchSummaryInfo } from "@/types/flights/flight-search-summary"
import { FlightSliceApi } from "@/types/flights/flight-slice-api"
import { FlightTransitPoint } from "@/types/flights/transit-points"
import { VendorCheckoutResponse } from "@/types/flights/vendor-checkout"
import { SimpleHttpError } from "@/types/http"
import { TripRef } from "@/types/trip"
import { Optional } from "@/types/util"
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import isEqual from "lodash/isEqual"
import last from "lodash/last"
import uniqBy from "lodash/uniqBy"

import { buildClientAxios } from "@/lib/client-axios"
import { beforeDayObject, browserToday, today } from "@/lib/date-utils"
import {
  cacheFlightSearchBackState,
  clearFlightSearchBackState,
  getCachedFlightSearchBackState,
} from "@/lib/ddb-flight"
import { httpIsCancelled } from "@/lib/exception-utils"
import { FlightApi } from "@/lib/flight-api"
import { buildFlightOptionsFromItinerary } from "@/lib/flight-booking-utils"
import {
  buildFlightOptions,
  buildFlightSearchInput,
  buildSearchInputFromState,
  buildSearchParams,
  buildUpdatedFilters,
  createEventsForFlightItinerary,
  emptyFlightSearchInput,
  FlightSearchSchemaErrors,
  initSearchValues,
  mapAirlinesToFlightItinerary,
  saveFlightItinerary,
  sendFlightBookingAnalyticsEvent,
  ValidatedSearchOptions,
  validateMultiCityDates,
  validateSearchOptions,
} from "@/lib/flight-utils"
import { consumePendingIosFlightHandoff } from "@/lib/ios-flight-handoff"
import { MemoryCache } from "@/lib/memory-cache"
import { mtLocation } from "@/lib/mt-location"
import { waitForSocketConnection } from "@/lib/storage/api/base-query"
import { selectAllPersistentEvents } from "@/lib/storage/itinerary-slice"
import { waitForPageInit } from "@/lib/storage/page-init-gate"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { selectTrip } from "@/lib/storage/trip-slice"
import { selectChatId, selectUser } from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { assertExists, assertPresent } from "@/lib/utils/assertions"
import { isEventWithMindtripFlightBooking } from "../itinerary-utils"
import { itineraryApi } from "./api/itinerary-api"
import { selectIsSplitView } from "./app-slice"
import detailSheetSlice, { closeDetailSheet } from "./detail-sheet-slice"
import { selectTripRef } from "./trip-slice"
import { selectVoiceModeOpen } from "./voice-chat-slice"

export type FlightSheetLoaders =
  | "bootstrap"
  | "return"
  | "contract"
  | "selected-itinerary"
  | "refresh"
  | "upgrade"
export type FlightProvider = "sabre" | "priceline"
type FlightSheetEntrySource = "web" | "ios-handoff"
export type FlightProviderStatus = "idle" | "loading" | "ready" | "error"

export type FlightTripTarget = { tripRef: TripRef } | { chatId: number }

export interface FlightSearchState {
  options: FlightSearchOptions
  locationLoading: boolean
  loading: boolean
  error: string
  airlines: Airline[]
  searchOptionsValid: boolean
  searchOptionErrors: FlightSearchSchemaErrors | undefined
  filters: FlightSearchFilters
  currentOptionIx: number
  showMax: number
  // The current step's display list; round-trip departures stay one card per outbound.
  currentFlightOptions: FlightOption[]
  // Round trips retain complete provider packages here to hydrate return choices.
  allSearchResults: FlightOption[]
  selectedOptionKeys: string[]
  selectedOptions: FlightOption[]
  // Accumulated related flight options across segment selections (for multi-city Sabre)
  relatedResults: FlightOption[]
  expiredPricesBanner: boolean
  offerUnavailableBanner: boolean
  passengersChangedBanner: boolean
  // The fare a price alert quoted when this search could not reproduce it.
  unavailableAlertPrice: number | null
}

function defaultSearchFilters(): FlightSearchFilters {
  return {
    values: {
      airlines: [],
      stops: "any",
      allAirlines: true,
      airports: [],
      allAirports: true,
      layoverAirports: [],
      allLayoverAirports: true,
      price: 10000,
      departureTimes: [0, 24],
      arrivalTimes: [0, 24],
      duration: 24,
    },
    options: {
      airlines: [],
      alliances: [],
      airports: [],
      layoverAirports: [],
      priceRange: [0, 10000],
      date: undefined,
      departureRange: [0, 24],
      arrivalRange: [0, 24],
      durationRange: [0, 24],
    },
  }
}

export interface FlightSearchSliceState {
  flightProvider: FlightProvider | undefined
  flightProviderStatus: FlightProviderStatus
  flightProviderScopeKey: string | undefined
  sheetOpen: boolean
  sheetExpanded: boolean
  sheetLoader: FlightSheetLoaders | null
  sheetStep: "search" | "contract"
  pendingSheetInput: FlightSearchInput | undefined
  lastAppliedSheetInput: FlightSearchInput | undefined
  contractItinerary: FlightItineraryApi | undefined
  buildingCheckout: boolean
  summaryError: string | undefined
  sheetError: string | undefined
  priceSummaryExpanded: boolean
  source: "mini_agent" | "direct_search" | undefined
  flightNotes: string | undefined
  flightPriceWatchQuoteRef: string | undefined
  // Watchable chat quotes the sheet was opened alongside; the contract reuses
  // whichever one quotes the selected flights.
  flightPriceWatchQuotes: FlightPriceWatchQuote[]
  // The chat's search watch, offered only while the sheet runs that search.
  flightSearchWatchQuote: FlightSearchWatchQuote | undefined
  sourceTripRef: TripRef | undefined
  loadingContractUpgradeItineraryId: string | undefined
  loadingContractTravelers: boolean
  // Active slice index on the contract upgrade picker (persisted across remounts)
  contractSliceIx: number
  sheetSearch: FlightSearchState
}

export type FlightPriceWatchQuote = {
  quoteRef: string
  sliceHashKeys: string[]
}

export type FlightSearchWatchQuote = {
  quoteRef: string
  includesRelevantAlternatives: boolean
}

type HydratedSearchContext = {
  airlines: Airline[]
  filters: FlightSearchFilters
  currentFlightOptions: FlightOption[]
  allSearchResults: FlightOption[]
}

export const flightSearchSliceInitialState: FlightSearchSliceState = {
  flightProvider: undefined,
  flightProviderStatus: "idle",
  flightProviderScopeKey: undefined,
  sheetOpen: false,
  sheetLoader: null,
  sheetExpanded: false,
  sheetStep: "search",
  pendingSheetInput: undefined,
  lastAppliedSheetInput: undefined,
  contractItinerary: undefined,
  buildingCheckout: false,
  summaryError: undefined,
  sheetError: undefined,
  priceSummaryExpanded: false,
  source: undefined,
  flightNotes: undefined,
  flightPriceWatchQuoteRef: undefined,
  flightPriceWatchQuotes: [],
  flightSearchWatchQuote: undefined,
  sourceTripRef: undefined,
  loadingContractUpgradeItineraryId: undefined,
  loadingContractTravelers: false,
  contractSliceIx: 0,
  sheetSearch: {
    expiredPricesBanner: false,
    offerUnavailableBanner: false,
    passengersChangedBanner: false,
    unavailableAlertPrice: null,
    options: initSearchValues(emptyFlightSearchInput()),
    locationLoading: false,
    loading: false,
    error: "",
    airlines: [],
    searchOptionsValid: false,
    searchOptionErrors: undefined,
    filters: defaultSearchFilters(),
    currentOptionIx: 0,
    showMax: 4,
    currentFlightOptions: [],
    allSearchResults: [],
    selectedOptionKeys: [],
    selectedOptions: [],
    relatedResults: [],
  },
}

// region selectors

export const selectFlightSearchState = (state: RootState) =>
  state.flightSearchSlice

export const selectSheetSearchInput = createSelector(
  selectFlightSearchState,
  (flightSearchSlice) => {
    if (flightSearchSlice.sheetOpen) {
      if (flightSearchSlice.pendingSheetInput) {
        return flightSearchSlice.pendingSheetInput
      }
      return buildSearchInputFromState(flightSearchSlice)
    }
    return undefined
  }
)
export const selectFlightSheetOpen = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetOpen
)

export const selectFlightSheetExpanded = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetExpanded
)

export const selectFlightSheetStep = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetStep
)

export const selectContractItinerary = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.contractItinerary
)

// The quote to watch (and to carry into checkout) for the flights on the
// contract page: a chat quote for exactly these slices, else the one the sheet
// was opened with.
export const selectContractPriceWatchQuoteRef = createSelector(
  selectFlightSearchState,
  (flightSearchState) => {
    const sliceHashKeys =
      flightSearchState.contractItinerary?.flight_slices.map(
        (slice) => slice.hash_key
      )
    const matching =
      sliceHashKeys &&
      flightSearchState.flightPriceWatchQuotes.find((quote) =>
        isEqual(quote.sliceHashKeys, sliceHashKeys)
      )
    return matching?.quoteRef ?? flightSearchState.flightPriceWatchQuoteRef
  }
)

export const selectBuildingCheckout = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.buildingCheckout
)

export const selectSummaryError = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.summaryError
)

export const selectSheetError = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetError
)

export const selectPriceSummaryExpanded = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.priceSummaryExpanded
)

export const selectFlightNotes = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.flightNotes
)

export const selectContractSliceIx = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.contractSliceIx
)

export const selectLoadingContractUpgradeItineraryId = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.loadingContractUpgradeItineraryId
)

export const selectLoadingContractTravelers = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.loadingContractTravelers
)

const selectItineraryEventsWithMindtripFlightBookings = createSelector(
  [selectAllPersistentEvents],
  (events) =>
    events
      .filter(isEventWithMindtripFlightBooking)
      .filter((event) => event.flight_leg != null)
)

export const selectFlightItineraryEventMatch = createSelector(
  [selectItineraryEventsWithMindtripFlightBookings, selectContractItinerary],
  (flightItineraryEvents, contractItinerary) => {
    if (contractItinerary) {
      return flightItineraryEvents.find(
        (fie) => fie.flight_itinerary?.id === contractItinerary.id
      )
    }
    return undefined
  }
)

export const selectSheetLoader = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetLoader
)

export const selectExpiredPricesBanner = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetSearch.expiredPricesBanner
)

export const selectOfferUnavailableBanner = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetSearch.offerUnavailableBanner
)

export const selectPassengersChangedBanner = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetSearch.passengersChangedBanner
)

export const selectUnavailableAlertPrice = createSelector(
  selectFlightSearchState,
  (flightSearchState) => flightSearchState.sheetSearch.unavailableAlertPrice
)

export const selectFlightTripTarget = createSelector(
  [selectFlightSearchState, selectChatId],
  (flightSearchState, chatId): FlightTripTarget | undefined => {
    if (flightSearchState.sourceTripRef) {
      return { tripRef: flightSearchState.sourceTripRef }
    }
    return chatId ? { chatId } : undefined
  }
)

export const selectDirectToAirline = (
  vendor: "priceline" | "sabre",
  bookable?: boolean
) => vendor === "sabre" && bookable !== true

// endregion

const flightSearchSlice = createSlice({
  name: "flightSearch",
  initialState: flightSearchSliceInitialState,
  reducers: {
    setFlightProviderLoading(
      state,
      action: PayloadAction<{ scopeKey: string }>
    ) {
      if (state.flightProviderScopeKey !== action.payload.scopeKey) {
        state.flightProvider = undefined
      }
      state.flightProviderStatus = "loading"
      state.flightProviderScopeKey = action.payload.scopeKey
    },
    setFlightProviderReady(
      state,
      action: PayloadAction<{ provider: FlightProvider; scopeKey: string }>
    ) {
      if (state.flightProviderScopeKey !== action.payload.scopeKey) {
        return
      }
      state.flightProvider = action.payload.provider
      state.flightProviderStatus = "ready"
      state.flightProviderScopeKey = action.payload.scopeKey
    },
    setFlightProviderError(
      state,
      action: PayloadAction<{ scopeKey: string | undefined }>
    ) {
      if (
        action.payload.scopeKey !== undefined &&
        state.flightProviderScopeKey !== action.payload.scopeKey
      ) {
        return
      }
      state.flightProvider = undefined
      state.flightProviderStatus = "error"
      state.flightProviderScopeKey = action.payload.scopeKey
    },
    setFlightSheetOpen(state, action: PayloadAction<boolean>) {
      state.sheetOpen = action.payload

      if (state.sheetOpen) {
        state.sheetExpanded = true
      } else {
        state.sheetSearch.expiredPricesBanner = false
        state.sheetSearch.offerUnavailableBanner = false
        state.sheetSearch.passengersChangedBanner = false
        state.sheetSearch.unavailableAlertPrice = null
        state.loadingContractUpgradeItineraryId = undefined
        state.loadingContractTravelers = false
      }
    },
    setSheetLoader(state, action: PayloadAction<FlightSheetLoaders | null>) {
      state.sheetLoader = action.payload
    },
    setLoadingContractUpgradeItineraryId(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.loadingContractUpgradeItineraryId = action.payload
    },
    clearLoadingContractUpgradeItineraryId(
      state,
      action: PayloadAction<string>
    ) {
      if (state.loadingContractUpgradeItineraryId === action.payload) {
        state.loadingContractUpgradeItineraryId = undefined
      }
    },
    setLoadingContractTravelers(state, action: PayloadAction<boolean>) {
      state.loadingContractTravelers = action.payload
    },
    setContractItinerary(
      state,
      action: PayloadAction<FlightItineraryApi | undefined>
    ) {
      state.contractItinerary = action.payload
    },
    setExpiredPricesBanner(state, action: PayloadAction<boolean>) {
      state.sheetSearch.expiredPricesBanner = action.payload
    },
    setOfferUnavailableBanner(state, action: PayloadAction<boolean>) {
      state.sheetSearch.offerUnavailableBanner = action.payload
    },
    setPassengersChangedBanner(state, action: PayloadAction<boolean>) {
      state.sheetSearch.passengersChangedBanner = action.payload
    },
    setUnavailableAlertPrice(state, action: PayloadAction<number | null>) {
      state.sheetSearch.unavailableAlertPrice = action.payload
    },
    setBuildingCheckout(state, action: PayloadAction<boolean>) {
      state.buildingCheckout = action.payload
    },
    setSummaryError(state, action: PayloadAction<string | undefined>) {
      state.summaryError = action.payload
    },
    setSheetError(state, action: PayloadAction<string | undefined>) {
      state.sheetError = action.payload
    },
    setSheetStep(state, action: PayloadAction<"search" | "contract">) {
      state.sheetStep = action.payload
    },

    setSheetExpanded(state, action: PayloadAction<boolean>) {
      state.sheetExpanded = action.payload
    },
    setPriceSummaryExpanded(state, action: PayloadAction<boolean>) {
      state.priceSummaryExpanded = action.payload
    },
    setContractSliceIx(state, action: PayloadAction<number>) {
      state.contractSliceIx = action.payload
    },
    setAirlines(state, action: PayloadAction<Airline[]>) {
      state.sheetSearch.airlines = action.payload
    },
    resetSelectedOptions(state) {
      state.sheetSearch.currentOptionIx = 0
      state.sheetSearch.selectedOptionKeys = []
      state.sheetSearch.selectedOptions = []
      state.sheetSearch.relatedResults = []
    },
    setFilters(state, action: PayloadAction<FlightSearchFilters>) {
      state.sheetSearch.filters = action.payload
    },
    setShowMax(state, action: PayloadAction<number>) {
      state.sheetSearch.showMax = action.payload
    },
    setCurrentOptionIx(state, action: PayloadAction<number>) {
      state.sheetSearch.currentOptionIx = action.payload
    },
    setSearchError(state, action: PayloadAction<string>) {
      state.sheetSearch.error = action.payload
    },
    setCurrentFlightOptions(state, action: PayloadAction<FlightOption[]>) {
      state.sheetSearch.currentFlightOptions = action.payload
    },
    setAllSearchResults(state, action: PayloadAction<FlightOption[]>) {
      state.sheetSearch.allSearchResults = action.payload
    },
    appendRelatedResults(state, action: PayloadAction<FlightOption[]>) {
      state.sheetSearch.relatedResults = [
        ...state.sheetSearch.relatedResults,
        ...action.payload,
      ]
    },
    clearRelatedResults(state) {
      state.sheetSearch.relatedResults = []
    },
    setSearchOrigin(
      state,
      action: PayloadAction<FlightTransitPoint | undefined>
    ) {
      state.sheetSearch.options.origin = action.payload
    },
    setSearchDestination(
      state,
      action: PayloadAction<FlightTransitPoint | undefined>
    ) {
      state.sheetSearch.options.destination = action.payload
    },
    setSearchSegments(
      state,
      action: PayloadAction<MultiCitySegment[] | undefined>
    ) {
      state.sheetSearch.options.segments = action.payload
    },
    setSelectedOptionKeys(state, action: PayloadAction<string[]>) {
      state.sheetSearch.selectedOptionKeys = action.payload
    },
    setSelectedOptions(state, action: PayloadAction<FlightOption[]>) {
      state.sheetSearch.selectedOptions = action.payload
    },
    setUserSelectedOptions(state, action: PayloadAction<FlightOption[]>) {
      state.sheetSearch.selectedOptions = action.payload
      state.sheetSearch.selectedOptionKeys = action.payload.map(
        (option) => option.flight_slice.hash_key
      )
    },
    setPendingSpendingSheetInput(
      state,
      action: PayloadAction<FlightSearchInput | undefined>
    ) {
      state.pendingSheetInput = action.payload
    },
    setLastAppliedSheetInput(
      state,
      action: PayloadAction<FlightSearchInput | undefined>
    ) {
      state.lastAppliedSheetInput = action.payload
    },
    setSource(
      state,
      action: PayloadAction<"mini_agent" | "direct_search" | undefined>
    ) {
      state.source = action.payload
    },
    setFlightNotes(state, action: PayloadAction<string | undefined>) {
      state.flightNotes = action.payload
    },
    setFlightPriceWatchQuoteRef(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.flightPriceWatchQuoteRef = action.payload
    },
    setFlightPriceWatchQuotes(
      state,
      action: PayloadAction<FlightPriceWatchQuote[]>
    ) {
      state.flightPriceWatchQuotes = action.payload
    },
    setFlightSearchWatchQuote(
      state,
      action: PayloadAction<FlightSearchWatchQuote | undefined>
    ) {
      state.flightSearchWatchQuote = action.payload
    },
    setSourceTripRef(state, action: PayloadAction<TripRef | undefined>) {
      state.sourceTripRef = action.payload
    },
    setFlightSearchOptions(state, action: PayloadAction<FlightSearchOptions>) {
      state.sheetSearch.options = {
        ...action.payload,
        range: normalizeRange(action.payload.range),
      }
    },
    setFlightOptionsFromInput(state, action: PayloadAction<FlightSearchInput>) {
      const initValues = initSearchValues(action.payload)
      state.sheetSearch.options = {
        ...initValues,
        // keep origin and destination if they are already set
        origin: initValues.origin ?? state.sheetSearch.options.origin,
        destination:
          initValues.destination ?? state.sheetSearch.options.destination,
        range: normalizeRange(initValues.range),
      }
    },
    startLocationLoading(state) {
      state.sheetSearch.locationLoading = true
    },
    finishLocationLoading(state) {
      state.sheetSearch.locationLoading = false
    },
    setSearchLoading(state, action: PayloadAction<boolean>) {
      state.sheetSearch.loading = action.payload
    },
    setSearchOptionsValid(
      state,
      action: PayloadAction<{
        valid: boolean
        errors: FlightSearchSchemaErrors | undefined
      }>
    ) {
      state.sheetSearch.searchOptionsValid = action.payload.valid
      state.sheetSearch.searchOptionErrors = action.payload.errors
    },
  },
  extraReducers: (builder) => {
    builder.addCase(detailSheetSlice.actions.open, (_, action) => {
      if (action.payload) {
        flightSearchSlice.actions.setFlightSheetOpen(false)
      }
    })
  },
})

// region helpers

function normalizeRange(range: FlightSearchOptions["range"]) {
  let from = range.from
  if (from && beforeDayObject(from, browserToday())) {
    from = undefined
  }
  let to = range.to
  if (to && from && beforeDayObject(to, from)) {
    to = undefined
  }
  return {
    from,
    to,
  }
}

async function departureSearch(
  searchParams: FlightSearchParams,
  flightApi: FlightApi
) {
  let res: FlightSearchResult | undefined = undefined
  const key = flightSearchCache.objToKey(searchParams)
  const cachePromise = flightSearchCache.get(key)
  if (cachePromise) {
    res = await cachePromise
  }

  if (!res) {
    const fetching = flightApi.departureSearch(searchParams)
    flightSearchCache.set(key, fetching, FLIGHT_SEARCH_CACHE_EXPIRES)
    try {
      res = await fetching
    } catch (e) {
      flightSearchCache.del(key)
      throw e
    }
  }

  return res
}

async function returnSearch(
  returnId: string,
  departureApiLogId: Optional<string>,
  flightApi: FlightApi
) {
  let res: FlightSearchResult | undefined = undefined
  const key = returnId

  const cached = flightSearchCache.get(key)

  if (cached) {
    res = await cached
  }

  if (!res) {
    const fetching = flightApi.returnSearch(returnId, departureApiLogId)

    flightSearchCache.set(returnId, fetching, FLIGHT_SEARCH_CACHE_EXPIRES)

    try {
      res = await fetching
    } catch (e) {
      flightSearchCache.del(key)
      throw e
    }
  }

  return res
}

function applySearchContext(
  context: HydratedSearchContext,
  dispatch: AppDispatch
) {
  dispatch(flightSearchSlice.actions.setFilters(context.filters))
  dispatch(flightSearchSlice.actions.setAirlines(context.airlines))
  dispatch(
    flightSearchSlice.actions.setCurrentFlightOptions(
      context.currentFlightOptions
    )
  )
  dispatch(
    flightSearchSlice.actions.setAllSearchResults(context.allSearchResults)
  )
}

// endregion

// region thunks

let sheetThunkAction: { abort: (reason?: string) => void } | undefined =
  undefined
let inFlightProviderRequest:
  | { scopeKey: string; promise: Promise<FlightProvider> }
  | undefined

const flightSearchCache = new MemoryCache<
  Promise<FlightSearchResult | undefined>
>()
const FLIGHT_SEARCH_CACHE_EXPIRES = 1000 * 60 * 30
const flightLocationCache = new MemoryCache<FlightTransitPoint>()

function buildFlightProviderScopeKey(state: RootState): string {
  return state.appSlice.orgid ?? "direct"
}

async function updateDepartureResults(
  searchOptions: ValidatedSearchOptions,
  getState: () => RootState,
  flightApi: FlightApi,
  signal: AbortSignal,
  dispatch: AppDispatch
): Promise<[FlightOption[], Airline[]]> {
  dispatch(flightSearchSlice.actions.setShowMax(4))

  const context = await buildDepartureSearchContext(
    searchOptions,
    getState,
    flightApi,
    signal
  )

  applySearchContext(context, dispatch)

  return [context.currentFlightOptions, context.airlines]
}

//Sabre has all the flights baked in one api call
function hasNextFlightOptions(selectedDeparture: FlightOption) {
  return (
    selectedDeparture.flight_slices &&
    selectedDeparture.flight_slices.length > 0
  )
}

async function updateReturnResults(
  selectedDeparture: FlightOption,
  allDepartureOptions: FlightOption[],
  prevAirlines: Airline[],
  getState: () => RootState,
  flightApi: FlightApi,
  signal: AbortSignal,
  dispatch: AppDispatch
): Promise<[FlightOption[], Airline[]]> {
  dispatch(flightSearchSlice.actions.setShowMax(4))

  const context = await buildReturnSearchContext(
    selectedDeparture,
    allDepartureOptions,
    prevAirlines,
    getState,
    flightApi,
    signal
  )

  applySearchContext(context, dispatch)

  return [context.currentFlightOptions, context.airlines]
}

// Helper function to create unique flight key based on flight details
function createFlightKey(flightSlice: FlightSliceApi): string {
  const legs = flightSlice.flight_legs
  const segmentKeys = legs
    .map(
      (leg: FlightLegApi) =>
        `${leg.airline_iata}${leg.flight_number}:${leg.departs_at.epoch_millis}`
    )
    .join(",")

  return `${flightSlice.departure_airport.code}${flightSlice.arrival_airport.code}:${segmentKeys}`
}

function handleFlightSearchErrors(error: unknown, dispatch: AppDispatch) {
  if (error instanceof FlightExpirationException) {
    void dispatch(refreshExpiredSearchWithBanner())
    return
  }

  let message = "Error retrieving flight results"
  if (error instanceof FlightDateOutOfRangeException) {
    message = error.message
  }

  console.error("error retrieving flight results", error)
  dispatch(flightSearchSlice.actions.setSearchError(message))
  dispatch(flightSearchSlice.actions.finishLocationLoading())
  dispatch(flightSearchSlice.actions.setSearchLoading(false))
}

function handleContractRefreshErrors(error: unknown, dispatch: AppDispatch) {
  if (error instanceof FlightExpirationException) {
    void dispatch(refreshExpiredSearchWithBanner())
    return
  }

  if (error instanceof FlightOfferUnavailableException) {
    dispatch(flightSearchSlice.actions.setOfferUnavailableBanner(true))
    void dispatch(refreshExpiredSearch())
    return
  }

  // NDC fares are priced for the traveler set they were shopped with, so a changed count needs a
  // new search. refreshContractWithTravelers has already written the new travelers into
  // sheetSearch.options, which is what refreshExpiredSearch searches from.
  if (error instanceof FlightPassengersChangedException) {
    dispatch(flightSearchSlice.actions.setPassengersChangedBanner(true))
    void dispatch(refreshExpiredSearch())
    return
  }

  let message = "Error fetching flight results"
  if (error instanceof FlightDateOutOfRangeException) {
    message = error.message
  }

  console.error(error)
  dispatch(flightSearchSlice.actions.setSheetError(message))
  dispatch(flightSearchSlice.actions.setSheetLoader(null))
}

function needsLazyHydrationForChange(
  optionIx: number,
  getState: () => RootState
) {
  const searchState = getState().flightSearchSlice.sheetSearch

  if (searchState.options.tripType === "round-trip" && optionIx === 1) {
    return searchState.currentFlightOptions.length === 0
  }

  if (searchState.options.tripType === "multi-city") {
    return searchState.allSearchResults.length === 0
  }

  return false
}

function needsLazyHydrationForUpgrade(state: RootState) {
  const searchState = state.flightSearchSlice.sheetSearch
  const contract = state.flightSearchSlice.contractItinerary
  return (
    contract?.vendor === "sabre" && searchState.allSearchResults.length === 0
  )
}

function findMatchingSelectedDeparture(
  selectedOption: FlightOption | undefined,
  allDepartureOptions: FlightOption[]
) {
  if (!selectedOption) return undefined

  return allDepartureOptions.find(
    (option) =>
      option.flight_slice.hash_key === selectedOption.flight_slice.hash_key
  )
}

function buildHydratedMultiCitySelections(
  keptOptions: FlightOption[],
  allSearchResults: FlightOption[]
) {
  return keptOptions.map((selectedOption, ix) => {
    return (
      allSearchResults.find(
        (option) =>
          option.segment_index === ix &&
          option.flight_slice.hash_key === selectedOption.flight_slice.hash_key
      ) ?? selectedOption
    )
  })
}

function hasHydratedMultiCityOptionsForSegment(
  optionIx: number,
  keptOptions: FlightOption[],
  allSearchResults: FlightOption[]
) {
  return allSearchResults.some((option) => {
    if (option.segment_index !== optionIx) return false

    return keptOptions.every(
      (selectedOption, ix) =>
        option.flight_slices?.[ix]?.hash_key ===
        selectedOption.flight_slice.hash_key
    )
  })
}

async function buildDepartureSearchContext(
  searchOptions: ValidatedSearchOptions,
  getState: () => RootState,
  flightApi: FlightApi,
  signal: AbortSignal
): Promise<HydratedSearchContext> {
  const optionAirlines: Airline[] = []

  const flightProvider = assertPresent(
    getState().flightSearchSlice.flightProvider
  )
  const searchParams = buildSearchParams(searchOptions, flightProvider)

  // Fetch all searches in parallel, but collect results without mutating shared state.
  // Each callback returns its batch; we merge sequentially after to avoid race conditions
  // where concurrent callbacks read/write a shared `updatedOptions` and lose results.
  // Sabre sends 3 parallel requests per route (max_connections 0/1/2), making this
  // essential. Priceline typically sends 1 request per route but benefits from the
  // same pattern when multi-airport cities produce multiple requests.
  const batchResults = await Promise.all(
    searchParams.map(async (sp) => {
      const res: FlightSearchResult = await departureSearch(sp, flightApi)

      if (signal.aborted) {
        throw new SimpleHttpError("Request aborted", {
          isCancelled: true,
        })
      }

      return {
        options: res.results,
        airlines: res.airlines,
      }
    })
  )

  const flightItineraryEvents =
    selectItineraryEventsWithMindtripFlightBookings(getState())

  const providerOptions = batchResults.flatMap((batch) => {
    optionAirlines.push(...batch.airlines)
    return batch.options
  })

  const cheapestByOutbound = new Map<string, FlightOption>()
  for (const option of providerOptions) {
    const outboundKey = createFlightKey(option.flight_slice)
    const current = cheapestByOutbound.get(outboundKey)
    if (!current || option.price.total < current.price.total) {
      cheapestByOutbound.set(outboundKey, option)
    }
  }
  const displayedBundleOptions = [...cheapestByOutbound.values()]

  let currentFlightOptions = buildFlightOptions(
    displayedBundleOptions,
    flightItineraryEvents
  )
  let allSearchResults =
    searchOptions.tripType === "round-trip"
      ? buildFlightOptions(providerOptions, flightItineraryEvents)
      : currentFlightOptions

  // For multi-city bundle results, expand each bundle option into per-segment
  // view options. Each view has flight_slice set to the segment's slice and is
  // tagged with segment_index, while keeping flight_slices intact so we can
  // correlate across segments when the user narrows down their selection.
  const totalSegments = searchOptions.segments?.length ?? 0
  if (searchOptions.tripType === "multi-city" && totalSegments > 0) {
    currentFlightOptions = currentFlightOptions.flatMap((opt) =>
      (opt.flight_slices ?? [opt.flight_slice]).map((slice, i) => ({
        ...opt,
        trip_type: "multi-city",
        flight_slice: slice,
        segment_index: i,
      }))
    )
    allSearchResults = currentFlightOptions
  }

  const user = selectUser(getState())
  const filterBaseDate = searchOptions.range.from
    ? searchOptions.range.from
    : today(user.time_zone)

  return {
    airlines: uniqBy(optionAirlines, "iata"),
    filters: buildUpdatedFilters(
      getState().flightSearchSlice.sheetSearch.filters,
      currentFlightOptions,
      filterBaseDate,
      uniqBy(optionAirlines, "iata")
    ),
    currentFlightOptions,
    allSearchResults,
  }
}

async function buildReturnSearchContext(
  selectedDeparture: FlightOption,
  allDepartureOptions: FlightOption[],
  prevAirlines: Airline[],
  getState: () => RootState,
  flightApi: FlightApi,
  signal: AbortSignal
): Promise<HydratedSearchContext> {
  const searchState = getState().flightSearchSlice.sheetSearch
  const filters = searchState.filters
  const flightItineraryEvents =
    selectItineraryEventsWithMindtripFlightBookings(getState())
  const user = selectUser(getState())

  let updatedOptions: FlightOption[]
  let airlines: Airline[]

  if (hasNextFlightOptions(selectedDeparture)) {
    // Create unique key for the selected departure flight
    const departureKey = createFlightKey(selectedDeparture.flight_slice)

    // Filter options that have the same departure flight
    const matchingOptions = allDepartureOptions.filter((option) => {
      if (!option.flight_slices || option.flight_slices.length < 2) return false
      if (!option.flight_slices[0]?.flight_legs) return false

      // Check if outbound slice matches selected departure
      const outboundKey = createFlightKey(
        option.flight_slices?.[0] ?? option.flight_slice
      )
      return outboundKey === departureKey
    })

    updatedOptions = matchingOptions.map((option) => ({
      ...option,
      flight_slice: assertExists(option.flight_slices?.[1]),
    }))

    updatedOptions = buildFlightOptions(updatedOptions, flightItineraryEvents)
    airlines = prevAirlines // Sabre: airlines already loaded from departure search
  } else {
    // Handle Priceline flights: use return_search API
    if (!selectedDeparture.vendor_data.return_id) {
      throw new Error("Missing vendor data")
    }

    const res = await returnSearch(
      selectedDeparture.vendor_data.return_id,
      selectedDeparture.api_log_id,
      flightApi
    )

    // manually check for cancellation as we want the above request to finish and get cached
    if (signal.aborted) {
      throw new SimpleHttpError("Request aborted", {
        isCancelled: true,
      })
    }

    updatedOptions = buildFlightOptions(res.results, flightItineraryEvents)
    airlines = res.airlines
  }

  const searchOptions = searchState.options
  const filterBaseDate = searchOptions.range.to
    ? searchOptions.range.to
    : today(user.time_zone)

  const updatedAirlines: Airline[] = uniqBy(
    [...prevAirlines, ...airlines],
    "iata"
  )

  return {
    currentFlightOptions: updatedOptions,
    allSearchResults: allDepartureOptions,
    airlines: updatedAirlines,
    filters: buildUpdatedFilters(
      filters,
      updatedOptions,
      filterBaseDate,
      updatedAirlines
    ),
  }
}

function buildSelectedOptionsFromPayload(
  option: FlightOptionPayload
): FlightOption[] {
  return option.flight_slices.map((flightSlice, ix) => ({
    ...option,
    flight_slice: flightSlice,
    slice_ticketing: assertExists(option.slice_ticketing[ix]),
    segment_index: option.trip_type === "multi-city" ? ix : undefined,
  }))
}

async function resolveSearchInputLocations(
  searchInput: FlightSearchInput,
  dispatch: AppDispatch,
  signal: AbortSignal
) {
  await dispatch(
    updateSearchOptionLocations({
      originIdentifier: searchInput.origin,
      destinationIdentifier: searchInput.destination,
    })
  )

  if (searchInput.segments?.length) {
    const segFlightApi = new FlightApi(buildClientAxios())
    const resolvedSegments: MultiCitySegment[] = await Promise.all(
      searchInput.segments.map(async (seg) => {
        let origin: FlightTransitPoint | undefined = undefined
        let destination: FlightTransitPoint | undefined = undefined
        if (seg.origin_ref) {
          origin = await getLocationByIdentifier(
            { entity_ref: seg.origin_ref },
            segFlightApi,
            signal
          )
        } else if (seg.origin_iata) {
          origin = await getLocationByIdentifier(
            { iata: seg.origin_iata },
            segFlightApi,
            signal
          )
        }
        if (seg.destination_ref) {
          destination = await getLocationByIdentifier(
            { entity_ref: seg.destination_ref },
            segFlightApi,
            signal
          )
        } else if (seg.destination_iata) {
          destination = await getLocationByIdentifier(
            { iata: seg.destination_iata },
            segFlightApi,
            signal
          )
        }
        return { origin, destination, date: seg.date }
      })
    )
    dispatch(flightSearchSlice.actions.setSearchSegments(resolvedSegments))
  }
}

/**
 * Builds the inline flight data payload for providers that don't use
 * contract_id lookup (e.g. Sabre). Returns undefined for Priceline itineraries.
 * When selectedOptions are available, uses them directly. Otherwise falls back
 * to reconstructing from the contract itinerary (e.g. revise flow).
 */
function buildInlineFlightData(
  contractItinerary: FlightItineraryApi,
  selectedOptions: FlightOption[],
  allResults: FlightOption[] = []
): FlightOptionPayload | undefined {
  if (contractItinerary.vendor === "priceline") return undefined

  const mergedVendorData = (
    option: FlightOption | FlightOptionPayload
  ): FlightOption["vendor_data"] => {
    const itineraryId = option.itinerary_id
    const matchingResult = itineraryId
      ? allResults.find((result) => result.itinerary_id === itineraryId)
      : undefined

    // The contract decides the checkout path: selecting a fare card or a re-shop can move the
    // itinerary between NDC and ATPCO, so its NDC handle (or its absence) always wins over the
    // search result's.
    const contractVendorData = contractItinerary.vendor_data

    return {
      ...option.vendor_data,
      offer_item_ids: contractVendorData?.offer_item_ids,
      shopping_response_id: contractVendorData?.shopping_response_id,
      applicable_pseudo_city_codes:
        contractVendorData?.applicable_pseudo_city_codes,
      additional_offers_refs:
        option.vendor_data.additional_offers_refs ??
        matchingResult?.vendor_data?.additional_offers_refs,
      validating_airline_codes:
        option.vendor_data.validating_airline_codes ??
        matchingResult?.vendor_data?.validating_airline_codes,
    }
  }

  const props = contractItinerary.properties
  const passengerProps = {
    adults: props.adults,
    children: props.children,
    infants_in_seat: props.infants_in_seat,
    infants_on_lap: props.infants_on_lap,
  }

  const selectedOption = last(selectedOptions)
  if (selectedOption) {
    return {
      ...selectedOption,
      vendor_data: mergedVendorData(selectedOption),
      flight_slices: selectedOptions.map((opt) => opt.flight_slice),
      slice_ticketing: props.slice_ticketing,
      price: props.price,
      passenger_pricing: props.passenger_pricing,
      segment_passenger_pricing: props.segment_passenger_pricing,
      upgrades: props.upgrades,
      bookable: props.bookable,
      additional_display_information: props.additional_display_information,
      contract_expiration: props.contract_expiration,
      ...passengerProps,
    }
  }

  const baseOption = buildFlightOptionsFromItinerary(contractItinerary)[0]
  if (baseOption) {
    return {
      ...baseOption,
      vendor_data: mergedVendorData(baseOption),
      flight_slices: contractItinerary.flight_slices,
      slice_ticketing: props.slice_ticketing,
      price: props.price,
      passenger_pricing: props.passenger_pricing,
      segment_passenger_pricing: props.segment_passenger_pricing,
      upgrades: props.upgrades,
      bookable: props.bookable,
      additional_display_information: props.additional_display_information,
      contract_expiration: props.contract_expiration,
      ...passengerProps,
    }
  }

  return undefined
}

async function selectContract(
  contractId: string,
  selectedOption: FlightOption | FlightOptionPayload | undefined,
  getState: () => RootState,
  dispatch: AppDispatch,
  signal: AbortSignal
) {
  dispatch(flightSearchSlice.actions.setSheetStep("contract"))
  if (getState().flightSearchSlice.sheetLoader !== "selected-itinerary") {
    dispatch(flightSearchSlice.actions.setSheetLoader("contract"))
  }
  dispatch(flightSearchSlice.actions.setSheetError(""))
  dispatch(flightSearchSlice.actions.setBuildingCheckout(false))
  const airlines = getState().flightSearchSlice.sheetSearch.airlines
  dispatch(flightSearchSlice.actions.setAirlines(airlines))

  const flightApi = new FlightApi(buildClientAxios())
  const isSabre = selectedOption?.vendor === "sabre"

  try {
    await waitForSocketConnection()

    let flightDataForContract: FlightOption | FlightOptionPayload | undefined =
      isSabre ? selectedOption : undefined
    if (isSabre && selectedOption) {
      const contractExpiration =
        selectContractItinerary(getState())?.properties.contract_expiration
      if (contractExpiration) {
        flightDataForContract = {
          ...selectedOption,
          contract_expiration: contractExpiration,
        } as FlightOptionPayload
      }
    }

    const travelers = getState().flightSearchSlice.sheetSearch.options.travelers
    const res = await flightApi.getContract(
      contractId,
      {
        signal: signal,
      },
      flightDataForContract,
      undefined,
      travelers
    )

    mapAirlinesToFlightItinerary(res, airlines)
    dispatch(flightSearchSlice.actions.setContractItinerary(res))
    dispatch(flightSearchSlice.actions.setSheetLoader(null))
  } catch (e) {
    // only need to handle non-cancel errors here, cancellations are no-ops
    if (!httpIsCancelled(e)) {
      handleContractRefreshErrors(e, dispatch)
    }
  }
}

async function getLocationByIdentifier(
  identifier: FlightLocationIdentifier,
  flightApi: FlightApi,
  signal: AbortSignal
): Promise<FlightTransitPoint> {
  // TODO: check all server methods that return transit points
  const key =
    "entity_ref" in identifier ? identifier.entity_ref : identifier.iata

  let location = flightLocationCache.get(key)
  if (!location) {
    if ("entity_ref" in identifier) {
      location = await flightApi.getLocation(identifier.entity_ref, { signal })
    } else {
      location = await flightApi.getLocationByIata(identifier.iata, { signal })
    }
    flightLocationCache.set(key, location)
  }

  return location
}

export const changeFlightOption = createAsyncThunk<
  void,
  number,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/changeFlightOption",
  async (optionIx, { dispatch, getState, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = {
      abort,
    }

    const searchState = getState().flightSearchSlice.sheetSearch

    dispatch(flightSearchSlice.actions.setSheetStep("search"))
    // The Continue quote only describes the flights it opened; re-picked
    // flights watch a matching chat quote, else checkout's own quote.
    dispatch(flightSearchSlice.actions.setFlightPriceWatchQuoteRef(undefined))

    if (searchState.options.tripType === "round-trip" && optionIx === 1) {
      try {
        dispatch(flightSearchSlice.actions.setSearchLoading(true))
        dispatch(flightSearchSlice.actions.setSearchError(""))

        let allDepartureOptions = searchState.allSearchResults
        let departureAirlines = searchState.airlines

        if (needsLazyHydrationForChange(optionIx, getState)) {
          const {
            valid,
            value: validated,
            errors,
          } = validateSearchOptions(searchState.options)
          dispatch(
            flightSearchSlice.actions.setSearchOptionsValid({
              valid,
              errors,
            })
          )
          if (!valid) {
            dispatch(flightSearchSlice.actions.setSearchLoading(false))
            return
          }

          const multiCityDateError = validateMultiCityDates(validated)
          if (multiCityDateError) {
            dispatch(
              flightSearchSlice.actions.setSearchError(multiCityDateError)
            )
            dispatch(flightSearchSlice.actions.setSearchLoading(false))
            return
          }

          const departureContext = await buildDepartureSearchContext(
            validated,
            getState,
            new FlightApi(buildClientAxios()),
            signal
          )
          dispatch(flightSearchSlice.actions.setShowMax(4))
          applySearchContext(departureContext, dispatch)
          allDepartureOptions = departureContext.allSearchResults
          departureAirlines = departureContext.airlines
        }

        dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
        const departureOption = searchState.selectedOptions[0]
        if (!departureOption) {
          throw new Error("No selected departure option")
        }

        const selectedDeparture = findMatchingSelectedDeparture(
          departureOption,
          allDepartureOptions
        )
        if (!selectedDeparture) {
          dispatch(flightSearchSlice.actions.setSearchLoading(false))
          void dispatch(refreshExpiredSearchWithBanner())
          return
        }

        dispatch(flightSearchSlice.actions.setCurrentOptionIx(1))
        dispatch(
          flightSearchSlice.actions.setUserSelectedOptions([selectedDeparture])
        )
        await updateReturnResults(
          selectedDeparture,
          allDepartureOptions,
          departureAirlines,
          getState,
          new FlightApi(buildClientAxios()),
          signal,
          dispatch
        )
        dispatch(flightSearchSlice.actions.setSearchLoading(false))
      } catch (e) {
        if (!httpIsCancelled(e)) {
          handleFlightSearchErrors(e, dispatch)
        }
      }
    } else if (searchState.options.tripType === "multi-city") {
      const shouldHydrate = needsLazyHydrationForChange(optionIx, getState)
      let allSearchResults = searchState.allSearchResults
      if (shouldHydrate) {
        dispatch(flightSearchSlice.actions.setSearchLoading(true))
        dispatch(flightSearchSlice.actions.setSearchError(""))

        const {
          valid,
          value: validated,
          errors,
        } = validateSearchOptions(searchState.options)
        dispatch(
          flightSearchSlice.actions.setSearchOptionsValid({
            valid,
            errors,
          })
        )
        if (!valid) {
          dispatch(flightSearchSlice.actions.setSearchLoading(false))
          return
        }

        const multiCityDateError = validateMultiCityDates(validated)
        if (multiCityDateError) {
          dispatch(flightSearchSlice.actions.setSearchError(multiCityDateError))
          dispatch(flightSearchSlice.actions.setSearchLoading(false))
          return
        }

        const departureContext = await buildDepartureSearchContext(
          validated,
          getState,
          new FlightApi(buildClientAxios()),
          signal
        )
        dispatch(flightSearchSlice.actions.setShowMax(4))
        applySearchContext(departureContext, dispatch)
        allSearchResults = departureContext.allSearchResults
        dispatch(flightSearchSlice.actions.setSearchLoading(false))
      }

      const keptOptions = searchState.selectedOptions.slice(0, optionIx)
      if (
        !hasHydratedMultiCityOptionsForSegment(
          optionIx,
          keptOptions,
          allSearchResults
        )
      ) {
        dispatch(flightSearchSlice.actions.setSearchLoading(false))
        void dispatch(refreshExpiredSearchWithBanner())
        return
      }

      dispatch(flightSearchSlice.actions.setCurrentOptionIx(optionIx))
      dispatch(
        flightSearchSlice.actions.setUserSelectedOptions(
          buildHydratedMultiCitySelections(keptOptions, allSearchResults)
        )
      )
      dispatch(
        flightSearchSlice.actions.setCurrentFlightOptions(allSearchResults)
      )
    } else {
      void dispatch(updateFlightSearchOptions(searchState.options))
    }
  }
)

function isLocationChange(
  identifier: FlightLocationIdentifier | undefined,
  transitPoint: FlightTransitPoint | undefined
) {
  if (identifier && !transitPoint) {
    return true
  }

  if (!identifier && transitPoint) {
    return true
  }

  if (
    identifier &&
    "entity_ref" in identifier &&
    identifier.entity_ref !== transitPoint?.id
  ) {
    return true
  }

  if (
    identifier &&
    "iata" in identifier &&
    (transitPoint?.type !== "airport" || identifier.iata !== transitPoint?.code)
  ) {
    return true
  }

  return false
}

// if we only have an identifier for origin or destination, we need to fetch the transit point object from the
// server. this is usually when being updated from the route or from the bot
const updateSearchOptionLocations = createAsyncThunk<
  void,
  {
    originIdentifier: FlightLocationIdentifier | undefined
    destinationIdentifier: FlightLocationIdentifier | undefined
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/updateSearchOptionLocations",
  async (
    { originIdentifier, destinationIdentifier },
    { dispatch, getState, signal }
  ) => {
    const flightApi = new FlightApi(buildClientAxios())

    // fetch origin transit point from identifier
    dispatch(flightSearchSlice.actions.startLocationLoading())

    const searchOptions = getState().flightSearchSlice.sheetSearch.options

    if (isLocationChange(originIdentifier, searchOptions.origin)) {
      let origin: FlightTransitPoint | undefined = undefined
      if (originIdentifier) {
        origin = await getLocationByIdentifier(
          originIdentifier,
          flightApi,
          signal
        )
      }

      dispatch(flightSearchSlice.actions.setSearchOrigin(origin))
    }

    // fetch destination transit point from identifier
    if (isLocationChange(destinationIdentifier, searchOptions.destination)) {
      let destination: FlightTransitPoint | undefined = undefined
      if (destinationIdentifier) {
        destination = await getLocationByIdentifier(
          destinationIdentifier,
          flightApi,
          signal
        )
      }
      dispatch(flightSearchSlice.actions.setSearchDestination(destination))
    }

    dispatch(flightSearchSlice.actions.finishLocationLoading())
  }
)

export const initializeFlightSheetRequest = createAsyncThunk<
  void,
  {
    searchInput: FlightSearchInput
    wasOpen: boolean
    previousAppliedRequest: FlightSearchInput | undefined
    initialContractOption?: FlightOptionPayload
    entrySource: FlightSheetEntrySource
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/initializeFlightSheetRequest",
  async (
    {
      searchInput,
      wasOpen,
      previousAppliedRequest,
      initialContractOption,
      entrySource,
    },
    { dispatch, abort, getState, signal }
  ) => {
    let shouldCommitLastApplied = false

    try {
      sheetThunkAction?.abort()
      sheetThunkAction = { abort }

      const state = getState().flightSearchSlice
      const scopeKey = buildFlightProviderScopeKey(getState())
      const providerReadyForScope =
        state.flightProviderStatus === "ready" &&
        state.flightProviderScopeKey === scopeKey
      const shouldReuseExistingSheet =
        wasOpen &&
        providerReadyForScope &&
        isEqual(searchInput, previousAppliedRequest) &&
        !state.sheetError

      if (shouldReuseExistingSheet) {
        return
      }

      dispatch(flightSearchSlice.actions.setSheetStep("search"))
      dispatch(flightSearchSlice.actions.setSheetLoader(null))
      dispatch(flightSearchSlice.actions.setSheetError(""))

      const providerResult = await dispatch(ensureFlightProviderLoaded())
      if (ensureFlightProviderLoaded.rejected.match(providerResult)) {
        dispatch(
          flightSearchSlice.actions.setSheetError(
            "Unable to load flight search right now. Please try again."
          )
        )
        return
      }

      const isContractStage =
        (searchInput.trip_type === "round-trip" &&
          searchInput.selected_options?.length === 2) ||
        (searchInput.trip_type === "one-way" &&
          searchInput.selected_options?.length === 1) ||
        (searchInput.trip_type === "multi-city" &&
          (searchInput.selected_options?.length ?? 0) >= 2)

      // Check for cached contract itinerary (from back-to-search navigation)
      // This preserves upgrade selections when navigating back from vendor checkout.
      // Cache key is the selected option hash_keys, so different flight options
      // within the same chat each get their own cache entry.
      // Only use the cache when navigating back from the booking page (revise=true),
      // not when opening fresh from a trip item, to avoid showing stale flights.
      const selectedOptionKeys = searchInput.selected_options ?? []
      const cachedContractItinerary = searchInput.revise
        ? await getCachedFlightSearchBackState(selectedOptionKeys)
        : null

      if (isContractStage && cachedContractItinerary) {
        // Use cached contract itinerary directly
        dispatch(flightSearchSlice.actions.setSheetStep("contract"))
        dispatch(
          flightSearchSlice.actions.setContractItinerary(
            cachedContractItinerary
          )
        )
        shouldCommitLastApplied = true
        await clearFlightSearchBackState(selectedOptionKeys)
        return
      }

      const directContractId = initialContractOption?.vendor_data.contract_id
      if (isContractStage && initialContractOption && directContractId) {
        dispatch(
          flightSearchSlice.actions.setSheetLoader(
            entrySource === "ios-handoff" ? "selected-itinerary" : "refresh"
          )
        )
        dispatch(flightSearchSlice.actions.setSheetStep("contract"))
        dispatch(flightSearchSlice.actions.setSearchLoading(true))
        dispatch(flightSearchSlice.actions.setSearchError(""))
        dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
        dispatch(flightSearchSlice.actions.setSelectedOptions([]))
        dispatch(flightSearchSlice.actions.setCurrentOptionIx(0))
        dispatch(
          flightSearchSlice.actions.setFlightOptionsFromInput(searchInput)
        )
        shouldCommitLastApplied = true

        await resolveSearchInputLocations(searchInput, dispatch, signal)

        const selectedOptions = buildSelectedOptionsFromPayload(
          initialContractOption
        )
        dispatch(flightSearchSlice.actions.setSelectedOptions(selectedOptions))
        dispatch(
          flightSearchSlice.actions.setCurrentOptionIx(selectedOptions.length)
        )

        await selectContract(
          directContractId,
          initialContractOption.vendor === "sabre"
            ? initialContractOption
            : undefined,
          getState,
          dispatch,
          signal
        )
        return
      }

      if (isContractStage) {
        // this should build a whole contract itinerary, so set the sheet loading
        dispatch(
          flightSearchSlice.actions.setSheetLoader(
            entrySource === "ios-handoff" ? "selected-itinerary" : "refresh"
          )
        )
        dispatch(flightSearchSlice.actions.setSheetStep("contract"))
      } else {
        if (
          searchInput.trip_type === "round-trip" &&
          searchInput.selected_options?.length === 1
        ) {
          dispatch(flightSearchSlice.actions.setSheetLoader("return"))
        } else {
          // might still be loading for a cancelled request, so set loading to false
          dispatch(flightSearchSlice.actions.setSheetLoader(null))
        }

        dispatch(flightSearchSlice.actions.setSheetStep("search"))
      }

      dispatch(flightSearchSlice.actions.setSearchLoading(true))
      dispatch(flightSearchSlice.actions.setSearchError(""))
      dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
      dispatch(flightSearchSlice.actions.setSelectedOptions([]))
      dispatch(flightSearchSlice.actions.setCurrentOptionIx(0))

      dispatch(flightSearchSlice.actions.setFlightOptionsFromInput(searchInput))
      shouldCommitLastApplied = true

      await resolveSearchInputLocations(searchInput, dispatch, signal)

      const flightApi = new FlightApi(buildClientAxios())

      const {
        valid,
        value: validated,
        errors,
      } = validateSearchOptions(
        getState().flightSearchSlice.sheetSearch.options
      )

      dispatch(
        flightSearchSlice.actions.setSearchOptionsValid({
          valid,
          errors,
        })
      )

      if (!valid) {
        if (isContractStage) {
          dispatch(flightSearchSlice.actions.setSheetStep("search"))
        }
        return
      }

      const multiCityDateError = validateMultiCityDates(validated)
      if (multiCityDateError) {
        dispatch(flightSearchSlice.actions.setSearchError(multiCityDateError))
        return
      }

      const [departureOptions] = await updateDepartureResults(
        validated,
        getState,
        flightApi,
        signal,
        dispatch
      )
      const departureOption = departureOptions.find(
        (x) => x.flight_slice.hash_key === selectedOptionKeys[0]
      )
      let resolvedContractSelection = false

      if (
        departureOption &&
        (searchInput.trip_type === "one-way" ||
          searchInput.trip_type === "round-trip")
      ) {
        if (searchInput.trip_type === "one-way" && isContractStage) {
          assertPresent(departureOption.vendor_data.contract_id)
        }
        await selectResolvedFlightOption(
          departureOption,
          getState,
          dispatch,
          signal,
          flightApi
        )

        if (searchInput.trip_type === "one-way") {
          resolvedContractSelection = isContractStage
        } else if (selectedOptionKeys.length === 2) {
          const returnOption =
            getState().flightSearchSlice.sheetSearch.currentFlightOptions.find(
              (option) => option.flight_slice.hash_key === selectedOptionKeys[1]
            )
          if (returnOption) {
            assertPresent(returnOption.vendor_data.contract_id)
            await selectResolvedFlightOption(
              returnOption,
              getState,
              dispatch,
              signal,
              flightApi
            )
            resolvedContractSelection = true
          }
        }
      } else if (departureOption && searchInput.trip_type === "multi-city") {
        // Multi-city bundle: find the bundle option whose flight_slices
        // match all selected hash_keys, then go straight to contract.
        const allResults =
          getState().flightSearchSlice.sheetSearch.allSearchResults
        // allSearchResults contains expanded per-segment views; find a
        // segment-0 view whose original bundle's slices match all keys.
        const bundleMatch = allResults.find(
          (opt) =>
            opt.segment_index === 0 &&
            opt.flight_slices &&
            opt.flight_slices.length === selectedOptionKeys.length &&
            selectedOptionKeys.every(
              (key, i) => opt.flight_slices?.[i]?.hash_key === key
            )
        )
        if (bundleMatch) {
          // Build per-segment selected options for the contract UI
          const selectedPerSegment = selectedOptionKeys.map((key, i) => {
            const segView = allResults.find(
              (opt) =>
                opt.segment_index === i &&
                opt.flight_slice.hash_key === key &&
                opt.itinerary_id === bundleMatch.itinerary_id &&
                opt.vendor_data.contract_id ===
                  bundleMatch.vendor_data.contract_id
            )
            return segView ?? bundleMatch
          })
          dispatch(
            flightSearchSlice.actions.setSelectedOptions(selectedPerSegment)
          )
          await selectContract(
            assertPresent(bundleMatch.vendor_data.contract_id),
            bundleMatch.vendor === "sabre" ? bundleMatch : undefined,
            getState,
            dispatch,
            signal
          )
          resolvedContractSelection = true
        }
      }

      if (!resolvedContractSelection) {
        if (isContractStage && searchInput.alert_price) {
          // Opened from a price alert whose itinerary we can no longer produce.
          // The alerted fare is the number the customer remembers, so report
          // against it instead of the generic expiry copy.
          dispatch(
            flightSearchSlice.actions.setUnavailableAlertPrice(
              searchInput.alert_price
            )
          )
          await dispatch(refreshExpiredSearch())
        } else if (isContractStage) {
          // Expected to find matching flights but didn't (e.g. hash key mismatch
          // or results expired). Auto-refresh with banner.
          await dispatch(refreshExpiredSearchWithBanner())
        } else {
          dispatch(flightSearchSlice.actions.setSheetStep("search"))
        }
      }
    } catch (error) {
      if (!httpIsCancelled(error)) {
        handleFlightSearchErrors(error, dispatch)
      }
    } finally {
      if (!signal.aborted) {
        if (shouldCommitLastApplied) {
          dispatch(
            flightSearchSlice.actions.setLastAppliedSheetInput(searchInput)
          )
        }
        dispatch(
          flightSearchSlice.actions.setPendingSpendingSheetInput(undefined)
        )
        dispatch(flightSearchSlice.actions.setSearchLoading(false))
        dispatch(flightSearchSlice.actions.setSheetLoader(null))
      }
    }
  }
)

// this is used to update the flight search sheet externally, ie from components outside the sheet.
// things like opening/closing due to route changes, or updating the sheet from a bot response. This is the main
// entry point for updating the flight search sheet externally.
export const openFlightSheetWithSearchInput = createAsyncThunk<
  void,
  {
    searchInput: FlightSearchInput | undefined
    flightNotes?: string
    initialContractOption?: FlightOptionPayload
    flightPriceWatchQuoteRef?: string
    flightPriceWatchQuotes?: FlightPriceWatchQuote[]
    flightSearchWatchQuote?: FlightSearchWatchQuote
    sourceTripRef?: TripRef
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/openFlightSheetWithSearchInput",
  async (
    {
      searchInput,
      flightNotes,
      initialContractOption,
      flightPriceWatchQuoteRef,
      flightPriceWatchQuotes = [],
      flightSearchWatchQuote,
      sourceTripRef,
    },
    { dispatch, getState }
  ) => {
    dispatch(flightSearchSlice.actions.setBuildingCheckout(false))

    if (!searchInput) {
      await dispatch(closeFlightSheet())
      return
    }

    void dispatch(closeDetailSheet())

    const stateBeforeOpen = getState().flightSearchSlice
    const wasOpen = stateBeforeOpen.sheetOpen
    const previousAppliedRequest = stateBeforeOpen.lastAppliedSheetInput
    const currentDestination = `${mtLocation.pathname}${mtLocation.search}`
    const entrySource: FlightSheetEntrySource = consumePendingIosFlightHandoff(
      currentDestination
    )
      ? "ios-handoff"
      : "web"

    // Opened from inside voice mode in split view, the sheet keeps to the
    // right half so the live voice overlay holds the left — full width would
    // cover it and evict voice to the mini-player (see
    // selectVoiceOverlayCoveredBySheet). Everywhere else the sheet keeps its
    // historical full-width open. Outside split view expansion doesn't change
    // coverage (the sheet is a takeover either way), so this only ever
    // narrows the split-view case.
    const rootState = getState()
    const openCollapsed =
      selectVoiceModeOpen(rootState) && selectIsSplitView(rootState)
    dispatch(flightSearchSlice.actions.setFlightSheetOpen(true))
    dispatch(flightSearchSlice.actions.setSheetExpanded(!openCollapsed))
    dispatch(flightSearchSlice.actions.setSheetStep("search"))
    dispatch(flightSearchSlice.actions.setSheetError(""))
    dispatch(flightSearchSlice.actions.setSheetLoader("bootstrap"))
    dispatch(
      flightSearchSlice.actions.setPendingSpendingSheetInput(searchInput)
    )
    dispatch(
      flightSearchSlice.actions.setSelectedOptionKeys(
        searchInput.selected_options ?? []
      )
    )
    dispatch(flightSearchSlice.actions.setSource(searchInput.source))
    dispatch(flightSearchSlice.actions.setFlightNotes(flightNotes))
    dispatch(
      flightSearchSlice.actions.setFlightPriceWatchQuoteRef(
        flightPriceWatchQuoteRef
      )
    )
    dispatch(
      flightSearchSlice.actions.setFlightPriceWatchQuotes(
        flightPriceWatchQuotes
      )
    )
    dispatch(
      flightSearchSlice.actions.setFlightSearchWatchQuote(
        flightSearchWatchQuote
      )
    )
    dispatch(flightSearchSlice.actions.setSourceTripRef(sourceTripRef))

    // Do not cache the gate promise across unrelated work.
    await waitForPageInit()

    const stateAfterWait = getState().flightSearchSlice
    if (!stateAfterWait.sheetOpen) {
      return
    }
    if (!isEqual(stateAfterWait.pendingSheetInput, searchInput)) {
      return
    }

    dispatch(flightSearchSlice.actions.setSheetLoader(null))

    await dispatch(
      initializeFlightSheetRequest({
        searchInput,
        wasOpen,
        previousAppliedRequest,
        initialContractOption,
        entrySource,
      })
    )
  }
)

async function runFlightSearch(
  searchOptions: FlightSearchOptions,
  getState: () => RootState,
  dispatch: AppDispatch,
  signal: AbortSignal
) {
  try {
    dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
    dispatch(flightSearchSlice.actions.setContractItinerary(undefined))
    dispatch(flightSearchSlice.actions.setFlightSearchOptions(searchOptions))

    const {
      valid,
      value: validated,
      errors,
    } = validateSearchOptions(searchOptions)
    dispatch(
      flightSearchSlice.actions.setSearchOptionsValid({
        valid,
        errors,
      })
    )

    const multiCityDateError = validateMultiCityDates(
      searchOptions as ValidatedSearchOptions
    )
    if (multiCityDateError) {
      dispatch(flightSearchSlice.actions.setSearchError(multiCityDateError))
      return
    }

    if (!valid) {
      return
    }

    dispatch(flightSearchSlice.actions.setSearchLoading(true))
    dispatch(flightSearchSlice.actions.setSearchError(""))

    const flightApi = new FlightApi(buildClientAxios())

    await updateDepartureResults(
      validated,
      getState,
      flightApi,
      signal,
      dispatch
    )

    dispatch(flightSearchSlice.actions.setSearchLoading(false))
  } catch (error) {
    if (!httpIsCancelled(error)) {
      handleFlightSearchErrors(error, dispatch)
    }
  }
}

export const updateFlightSearchOptions = createAsyncThunk<
  void,
  FlightSearchOptions,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/updateFlightSearchOptions",
  async (searchOptions, { getState, dispatch, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = { abort }

    if (
      !isEqual(searchOptions, getState().flightSearchSlice.sheetSearch.options)
    ) {
      dispatch(flightSearchSlice.actions.setFlightSearchWatchQuote(undefined))
    }
    dispatch(flightSearchSlice.actions.resetSelectedOptions())
    await runFlightSearch(searchOptions, getState, dispatch, signal)
  }
)

export const refreshFlightSearchOptions = createAsyncThunk<
  void,
  FlightSearchOptions,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/refreshFlightSearchOptions",
  async (searchOptions, { getState, dispatch, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = { abort }

    dispatch(flightSearchSlice.actions.setSelectedOptions([]))
    dispatch(flightSearchSlice.actions.clearRelatedResults())
    dispatch(flightSearchSlice.actions.setCurrentOptionIx(0))
    await runFlightSearch(searchOptions, getState, dispatch, signal)

    if (!signal.aborted) {
      dispatch(flightSearchSlice.actions.setSheetLoader(null))
    }
  }
)

export const warmFlightSearchCache = createAsyncThunk<
  void,
  FlightSearchSummaryInfo | undefined,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/warmFlightSearchCache",
  async (searchInfo, { dispatch, getState, signal }) => {
    try {
      if (!searchInfo || searchInfo.trip_type === "multi-city") {
        return
      }

      await waitForPageInit()

      const trip = selectTrip(getState())
      const searchInput = buildFlightSearchInput(searchInfo, trip)
      const topFlightsHashKeys: string[] =
        searchInfo?.top_flights_hash_keys ?? []

      const flightApi = new FlightApi(buildClientAxios())
      const searchOptions = initSearchValues(searchInput)

      if (searchInput.origin) {
        searchOptions.origin = await getLocationByIdentifier(
          searchInput.origin,
          flightApi,
          signal
        )
      }

      if (searchInput.destination) {
        searchOptions.destination = await getLocationByIdentifier(
          searchInput.destination,
          flightApi,
          signal
        )
      }

      const { valid, value: validated } = validateSearchOptions(searchOptions)

      if (valid) {
        const warmProvider = await dispatch(
          ensureFlightProviderLoaded()
        ).unwrap()
        const searchParams = buildSearchParams(validated, warmProvider)
        const allRes = await Promise.all(
          searchParams.map(async (sp) => {
            return await departureSearch(sp, flightApi)
          })
        )

        const topOptions = allRes
          .flatMap((ar) => ar.results)
          .filter((r) => topFlightsHashKeys.includes(r.flight_slice.hash_key))

        await Promise.all(
          topOptions.map(async (option) => {
            if (option.vendor_data.return_id) {
              await returnSearch(
                option.vendor_data.return_id,
                option.api_log_id,
                flightApi
              )
            }
          })
        )
      }
    } catch (e) {
      console.error("failed to warm flight search cache", e)
    }
  }
)

function buildSelectedOptionsThroughCurrentIndex(
  selectedOptions: FlightOption[],
  currentOptionIx: number,
  option: FlightOption
): FlightOption[] {
  return [...selectedOptions.slice(0, currentOptionIx), option]
}

async function selectResolvedFlightOption(
  option: FlightOption,
  getState: () => RootState,
  dispatch: AppDispatch,
  signal: AbortSignal,
  flightApi: FlightApi
) {
  const searchState = getState().flightSearchSlice.sheetSearch
  const updated = buildSelectedOptionsThroughCurrentIndex(
    searchState.selectedOptions,
    searchState.currentOptionIx,
    option
  )

  if (
    searchState.options.tripType === "round-trip" &&
    searchState.currentOptionIx === 0
  ) {
    dispatch(flightSearchSlice.actions.setSearchLoading(true))
    dispatch(flightSearchSlice.actions.setSearchError(""))
    dispatch(flightSearchSlice.actions.setSelectedOptions(updated))
    dispatch(flightSearchSlice.actions.setCurrentOptionIx(1))
    dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
    await updateReturnResults(
      option,
      searchState.allSearchResults,
      searchState.airlines,
      getState,
      flightApi,
      signal,
      dispatch
    )
    return
  }

  dispatch(flightSearchSlice.actions.setSelectedOptions(updated))
  const contractId = option.vendor_data.contract_id
  if (!contractId) {
    return
  }

  const existingContract = getState().flightSearchSlice.contractItinerary
  const selectedHashKeys = updated.map(
    (selected) => selected.flight_slice.hash_key
  )
  const contractMatchesSelection =
    existingContract &&
    existingContract.flight_slices.length === selectedHashKeys.length &&
    existingContract.flight_slices.every(
      (slice, ix) => slice.hash_key === selectedHashKeys[ix]
    )

  if (contractMatchesSelection) {
    dispatch(flightSearchSlice.actions.setSheetStep("contract"))
    dispatch(flightSearchSlice.actions.setSheetLoader(null))
    return
  }

  await selectContract(
    contractId,
    option.vendor === "sabre" ? option : undefined,
    getState,
    dispatch,
    signal
  )
}

export const selectFlightOption = createAsyncThunk<
  void,
  FlightOption,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/selectFlightOption",
  async (option, { getState, dispatch, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = { abort }

    try {
      const searchState = getState().flightSearchSlice.sheetSearch
      const totalSegments = searchState.options.segments?.length ?? 0
      const selectedOptions = buildSelectedOptionsThroughCurrentIndex(
        searchState.selectedOptions,
        searchState.currentOptionIx,
        option
      )
      dispatch(
        flightSearchSlice.actions.setSelectedOptionKeys(
          selectedOptions.map((selected) => selected.flight_slice.hash_key)
        )
      )

      if (searchState.options.tripType === "multi-city") {
        if (searchState.currentOptionIx < totalSegments - 1) {
          // Multi-city bundle: advance to next segment. The UI filters
          // currentFlightOptions by segment_index and previously selected slices.
          dispatch(
            flightSearchSlice.actions.setUserSelectedOptions(selectedOptions)
          )
          dispatch(
            flightSearchSlice.actions.setCurrentOptionIx(
              searchState.currentOptionIx + 1
            )
          )
        } else {
          // TODO(v2): Build combined FlightOptionPayload for multi-city split-ticket checkout
          await selectResolvedFlightOption(
            option,
            getState,
            dispatch,
            signal,
            new FlightApi(buildClientAxios())
          )
        }
      } else {
        await selectResolvedFlightOption(
          option,
          getState,
          dispatch,
          signal,
          new FlightApi(buildClientAxios())
        )
      }

      dispatch(flightSearchSlice.actions.setSearchLoading(false))
    } catch (error) {
      if (!httpIsCancelled(error)) {
        handleFlightSearchErrors(error, dispatch)
      }
    }
  }
)

export const selectContractUpgrade = createAsyncThunk<
  void,
  {
    upgrade: FlightContractUpgrade
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "flightSearch/selectContractUpgrade",
  async ({ upgrade }, { dispatch, getState, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = { abort }

    try {
      dispatch(
        flightSearchSlice.actions.setLoadingContractUpgradeItineraryId(
          upgrade.itinerary_id
        )
      )
      dispatch(flightSearchSlice.actions.setSummaryError(undefined))
      const flightApi = new FlightApi(buildClientAxios())
      const selectedOptions =
        getState().flightSearchSlice.sheetSearch.selectedOptions
      const searchState = getState().flightSearchSlice.sheetSearch
      const currentContractItinerary = selectContractItinerary(getState())

      // Check if Sabre from current contract itinerary (handles revise case where selectedOptions may be empty)
      const isSabre = currentContractItinerary?.vendor === "sabre"

      if (isSabre && needsLazyHydrationForUpgrade(getState())) {
        const {
          valid,
          value: validated,
          errors,
        } = validateSearchOptions(searchState.options)
        dispatch(
          flightSearchSlice.actions.setSearchOptionsValid({
            valid,
            errors,
          })
        )
        if (!valid) {
          dispatch(flightSearchSlice.actions.setSheetLoader(null))
          return
        }

        const multiCityDateError = validateMultiCityDates(validated)
        if (multiCityDateError) {
          dispatch(flightSearchSlice.actions.setSheetError(multiCityDateError))
          dispatch(flightSearchSlice.actions.setSheetLoader(null))
          return
        }

        const departureContext = await buildDepartureSearchContext(
          validated,
          getState,
          flightApi,
          signal
        )
        applySearchContext(departureContext, dispatch)
      }

      const allResults =
        getState().flightSearchSlice.sheetSearch.allSearchResults

      // For Sabre, build flight_slices from selected options or from current contract itinerary
      const flightDataForSabre =
        isSabre && currentContractItinerary
          ? buildInlineFlightData(
              currentContractItinerary,
              selectedOptions,
              allResults
            )
          : undefined

      const contractItinerary = await flightApi.getContract(
        upgrade.vendor_data.contract_id,
        {
          signal: signal,
        },
        flightDataForSabre,
        isSabre ? upgrade : undefined
      )
      mapAirlinesToFlightItinerary(
        contractItinerary,
        getState().flightSearchSlice.sheetSearch.airlines
      )
      dispatch(
        flightSearchSlice.actions.setContractItinerary(contractItinerary)
      )

      sendFlightBookingAnalyticsEvent(
        "FlightUpgradeSelect",
        contractItinerary,
        undefined,
        upgrade
      )

      dispatch(flightSearchSlice.actions.setSheetLoader(null))
    } catch (e) {
      if (!httpIsCancelled(e)) {
        handleContractRefreshErrors(e, dispatch)
      }
    } finally {
      dispatch(
        flightSearchSlice.actions.clearLoadingContractUpgradeItineraryId(
          upgrade.itinerary_id
        )
      )
    }
  }
)

export const refreshContractWithTravelers = createAsyncThunk<
  void,
  { travelers: FlightTravelers },
  { dispatch: AppDispatch; state: RootState }
>(
  "flightSearch/refreshContractWithTravelers",
  async ({ travelers }, { dispatch, getState, abort, signal }) => {
    sheetThunkAction?.abort()
    sheetThunkAction = { abort }

    try {
      const state = getState().flightSearchSlice
      const currentContractItinerary = selectContractItinerary(getState())
      if (
        !currentContractItinerary ||
        currentContractItinerary.vendor !== "sabre"
      ) {
        dispatch(flightSearchSlice.actions.setSheetLoader(null))
        return
      }
      dispatch(flightSearchSlice.actions.setLoadingContractTravelers(true))

      const { selectedOptions, allSearchResults, options } = state.sheetSearch
      const flightData = buildInlineFlightData(
        currentContractItinerary,
        selectedOptions,
        allSearchResults
      )
      if (!flightData) {
        return
      }

      dispatch(flightSearchSlice.actions.resetSelectedOptions())
      dispatch(
        flightSearchSlice.actions.setFlightSearchOptions({
          ...options,
          travelers,
        })
      )

      const flightApi = new FlightApi(buildClientAxios())
      const contractItinerary = await flightApi.getContract(
        undefined,
        { signal },
        flightData,
        undefined,
        travelers
      )

      mapAirlinesToFlightItinerary(
        contractItinerary,
        state.sheetSearch.airlines
      )
      dispatch(
        flightSearchSlice.actions.setContractItinerary(contractItinerary)
      )
      void cacheFlightSearchBackState(contractItinerary)
    } catch (e) {
      if (!httpIsCancelled(e)) {
        if (e instanceof FlightOfferUnavailableException) {
          dispatch(flightSearchSlice.actions.setContractItinerary(undefined))
          dispatch(flightSearchSlice.actions.setSheetStep("search"))
          dispatch(flightSearchSlice.actions.setSheetError(e.message))
        } else {
          handleContractRefreshErrors(e, dispatch)
        }
      }
    } finally {
      dispatch(flightSearchSlice.actions.setLoadingContractTravelers(false))
    }
  }
)

export const refreshExpiredSearch = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/refreshExpiredSearch", async (_, { dispatch, getState }) => {
  flightSearchCache.clear()
  dispatch(flightSearchSlice.actions.setSheetLoader("refresh"))

  const pendingSheetInput = getState().flightSearchSlice.pendingSheetInput
  if (pendingSheetInput) {
    dispatch(
      flightSearchSlice.actions.setLastAppliedSheetInput(pendingSheetInput)
    )
    dispatch(flightSearchSlice.actions.setPendingSpendingSheetInput(undefined))
  }

  // Clear all stale data before starting a fresh search
  dispatch(flightSearchSlice.actions.setContractItinerary(undefined))
  dispatch(flightSearchSlice.actions.setFlightPriceWatchQuoteRef(undefined))
  dispatch(flightSearchSlice.actions.setSheetStep("search"))
  dispatch(flightSearchSlice.actions.setCurrentFlightOptions([]))
  dispatch(flightSearchSlice.actions.setAllSearchResults([]))

  // Trigger a fresh search with current search options
  const currentSheetSearchOptions =
    getState().flightSearchSlice.sheetSearch.options
  await dispatch(refreshFlightSearchOptions(currentSheetSearchOptions))
})

export const refreshExpiredSearchWithBanner = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/refreshExpiredSearchWithBanner", async (_, { dispatch }) => {
  dispatch(flightSearchSlice.actions.setExpiredPricesBanner(true))
  await dispatch(refreshExpiredSearch())
})

export type BuildCheckoutResult = FlightBooking & {
  vendor_checkout?: VendorCheckoutResponse
}

const RECREATE_BOOKING_STATUSES: FlightBookingStatus[] = [
  "error",
  "expired",
  "ticketing-error",
]

export const buildCheckout = createAsyncThunk<
  BuildCheckoutResult | undefined,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/buildCheckout", async (_, { dispatch, getState }) => {
  try {
    const contractItinerary = selectContractItinerary(getState())

    if (contractItinerary == null) {
      console.error("contract itinerary is null")
      return
    }

    dispatch(flightSearchSlice.actions.setBuildingCheckout(true))
    dispatch(flightSearchSlice.actions.setSummaryError(undefined))

    const target = assertPresent(
      selectFlightTripTarget(getState()),
      "flight checkout needs a trip or a chat"
    )
    const flightPriceWatchQuoteRef =
      selectContractPriceWatchQuoteRef(getState())
    const airlines = getState().flightSearchSlice.sheetSearch.airlines
    const sheetOptions =
      getState().flightSearchSlice.sheetSearch.selectedOptions

    const flightApi = new FlightApi(buildClientAxios())

    const api_log_ids = sheetOptions
      .map((so) => so.api_log_id)
      .filter((id): id is string => id != null)

    const isSabre = contractItinerary.vendor === "sabre"
    const inlineFlightData = buildInlineFlightData(
      contractItinerary,
      sheetOptions,
      getState().flightSearchSlice.sheetSearch.allSearchResults
    )

    let flightItinerary = await saveFlightItinerary(
      contractItinerary,
      airlines,
      flightApi,
      api_log_ids.length > 0 ? api_log_ids : undefined,
      inlineFlightData
    )

    let flightBooking = flightItinerary.flight_booking
    if (
      flightBooking != null &&
      RECREATE_BOOKING_STATUSES.includes(flightBooking.status)
    ) {
      flightItinerary = await saveFlightItinerary(
        {
          ...flightItinerary,
          id: null,
          flight_booking: undefined,
          created_at: null,
          updated_at: null,
          latest_chat_id: null,
          trip_ref: null,
        },
        airlines,
        flightApi,
        api_log_ids.length > 0 ? api_log_ids : undefined,
        inlineFlightData
      )
      flightBooking = flightItinerary.flight_booking
      dispatch(flightSearchSlice.actions.setContractItinerary(flightItinerary))
    }

    // create an itinerary event for this if it doesn't exist
    await createEventsForFlightItinerary(
      flightItinerary,
      target,
      flightApi,
      dispatch
    )

    const checkoutHasPriceWatchQuote =
      flightBooking?.properties.flight_price_watch_quote_ref != null
    if (
      flightBooking == null ||
      flightPriceWatchQuoteRef ||
      !checkoutHasPriceWatchQuote
    ) {
      const flightItineraryId = assertExists(flightItinerary.id)
      flightBooking = await flightApi.createFlightBooking({
        flight_itinerary_id: flightItineraryId,
        chat_id: "chatId" in target ? target.chatId : undefined,
        ...(flightPriceWatchQuoteRef
          ? { flight_quote_ref: flightPriceWatchQuoteRef }
          : {}),
      })
    }

    // For Sabre, get checkout info with iframe URL
    if (
      isSabre &&
      flightBooking &&
      flightBooking.id != null &&
      PRE_SUBMIT_STATUSES.includes(flightBooking.status)
    ) {
      const travelers =
        getState().flightSearchSlice.sheetSearch.options.travelers
      const vendorCheckout = await flightApi.vendorCheckout(
        flightBooking.id,
        travelers
      )
      return {
        ...flightBooking,
        vendor_checkout: vendorCheckout,
      }
    }

    return flightBooking
  } catch (e) {
    if (e instanceof FlightExpirationException) {
      dispatch(flightSearchSlice.actions.setBuildingCheckout(false))
      void dispatch(refreshExpiredSearchWithBanner())
    } else if (e instanceof FlightPassengersChangedException) {
      dispatch(flightSearchSlice.actions.setBuildingCheckout(false))
      dispatch(flightSearchSlice.actions.setPassengersChangedBanner(true))
      void dispatch(refreshExpiredSearch())
    } else {
      console.error(e)
      // Drop the back-state cache so a later return to the contract fetches fresh data
      // instead of resuming against the failed checkout attempt.
      const contractItinerary = selectContractItinerary(getState())
      const selectedOptionKeys =
        contractItinerary?.flight_slices?.map((s) => s.hash_key) ?? []
      void clearFlightSearchBackState(selectedOptionKeys)
      dispatch(
        flightSearchSlice.actions.setSummaryError("Error adding flight to trip")
      )
      dispatch(flightSearchSlice.actions.setBuildingCheckout(false))
      return
    }
  }
})

export const persistContractItinerary = createAsyncThunk<
  number | undefined,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "flightSearch/persistContractItinerary",
  async (_, { dispatch, getState }) => {
    const contractItinerary = selectContractItinerary(getState())
    if (!contractItinerary) return undefined
    if (contractItinerary.id != null) return contractItinerary.id

    const airlines = getState().flightSearchSlice.sheetSearch.airlines
    const selectedOptions =
      getState().flightSearchSlice.sheetSearch.selectedOptions
    const api_log_ids = selectedOptions
      .map((so) => so.api_log_id)
      .filter((id): id is string => id != null)
    const flightApi = new FlightApi(buildClientAxios())

    const inlineFlightData = buildInlineFlightData(
      contractItinerary,
      selectedOptions,
      getState().flightSearchSlice.sheetSearch.allSearchResults
    )

    const updated = await saveFlightItinerary(
      contractItinerary,
      airlines,
      flightApi,
      api_log_ids.length > 0 ? api_log_ids : undefined,
      inlineFlightData
    )
    dispatch(flightSearchSlice.actions.setContractItinerary(updated))
    return updated.id ?? undefined
  }
)

export const addToTrip = createAsyncThunk<
  { success: boolean },
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/addToTrip", async (_, { dispatch, getState }) => {
  try {
    const contractItinerary = assertPresent(selectContractItinerary(getState()))
    const selectedOptions =
      getState().flightSearchSlice.sheetSearch.selectedOptions
    const api_log_ids = selectedOptions
      .map((so) => so.api_log_id)
      .filter((id): id is string => id != null)
    const airlines = getState().flightSearchSlice.sheetSearch.airlines
    const target = assertPresent(
      selectFlightTripTarget(getState()),
      "adding a flight needs a trip or a chat"
    )
    const flightApi = new FlightApi(buildClientAxios())

    const inlineFlightData = buildInlineFlightData(
      contractItinerary,
      selectedOptions,
      getState().flightSearchSlice.sheetSearch.allSearchResults
    )

    let updatedItinerary = contractItinerary
    if (contractItinerary.id == null) {
      updatedItinerary = await saveFlightItinerary(
        contractItinerary,
        airlines,
        flightApi,
        api_log_ids.length > 0 ? api_log_ids : undefined,
        inlineFlightData
      )
      dispatch(flightSearchSlice.actions.setContractItinerary(updatedItinerary))
    }

    await createEventsForFlightItinerary(
      updatedItinerary,
      target,
      flightApi,
      dispatch
    )

    sendFlightBookingAnalyticsEvent("TripFlightAdd", updatedItinerary)

    return { success: true }
  } catch (e) {
    if (e instanceof FlightExpirationException) {
      void dispatch(refreshExpiredSearchWithBanner())
    } else {
      console.error(e)
      toastManager.add({
        type: "error",
        description:
          "Sorry, there was a problem adding this flight to your trip",
      })
    }
    return { success: false }
  }
})

export const removeFromTrip = createAsyncThunk<
  { success: boolean },
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/removeFromTrip", async (_, { dispatch, getState }) => {
  try {
    const target = assertPresent(
      selectFlightTripTarget(getState()),
      "removing a flight needs a trip or a chat"
    )
    const contractItinerary = assertPresent(selectContractItinerary(getState()))
    const contractItineraryId = assertPresent(contractItinerary.id)
    const flightApi = new FlightApi(buildClientAxios())

    const response =
      "tripRef" in target
        ? await flightApi.deleteFlightFromTrip(
            target.tripRef,
            contractItineraryId
          )
        : await flightApi.deleteEventsForFlight(
            target.chatId,
            contractItineraryId
          )
    const eventIds = response.deleted_itinerary_events.map((e) => e.id)
    const tripRef = selectTripRef(getState())
    if (tripRef) {
      dispatch(
        itineraryApi.util.updateQueryData(
          "getItinerary",
          { tripRef },
          (draft) => {
            if (!draft) return
            draft.events = draft.events.filter((e) => !eventIds.includes(e.id))
          }
        )
      )
    }

    sendFlightBookingAnalyticsEvent("TripFlightRemove", contractItinerary)

    return { success: true }
  } catch (e) {
    console.error(e)
    toastManager.add({
      type: "error",
      description:
        "Sorry, there was a problem removing this flight from your trip",
    })
    return { success: false }
  }
})

// endregion

export type FlightSearchSlice = typeof flightSearchSlice

export const selectFlightProvider = (
  state: RootState
): FlightProvider | undefined => state.flightSearchSlice.flightProvider

export const selectFlightProviderStatus = (
  state: RootState
): FlightProviderStatus => state.flightSearchSlice.flightProviderStatus

export const ensureFlightProviderLoaded = createAsyncThunk<
  FlightProvider,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: "request_failed"
  }
>(
  "flightSearch/ensureFlightProviderLoaded",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const scopeKey = buildFlightProviderScopeKey(getState())
    const state = getState().flightSearchSlice
    if (
      state.flightProviderStatus === "ready" &&
      state.flightProvider &&
      state.flightProviderScopeKey === scopeKey
    ) {
      return state.flightProvider
    }

    const existingProviderRequest = inFlightProviderRequest
    if (existingProviderRequest?.scopeKey === scopeKey) {
      try {
        return await existingProviderRequest.promise
      } catch {
        return rejectWithValue("request_failed")
      }
    }

    dispatch(flightSearchSlice.actions.setFlightProviderLoading({ scopeKey }))

    const request = new FlightApi(buildClientAxios())
      .getConfig()
      .then(({ provider }) => {
        dispatch(
          flightSearchSlice.actions.setFlightProviderReady({
            provider,
            scopeKey,
          })
        )
        return provider
      })
      .catch((error: unknown) => {
        dispatch(flightSearchSlice.actions.setFlightProviderError({ scopeKey }))
        throw error instanceof Error ? error : new Error(`${error}`)
      })
      .finally(() => {
        if (inFlightProviderRequest?.scopeKey === scopeKey) {
          inFlightProviderRequest = undefined
        }
      })

    inFlightProviderRequest = { scopeKey, promise: request }

    try {
      return await request
    } catch {
      return rejectWithValue("request_failed")
    }
  }
)

export const closeFlightSheet = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("flightSearch/closeFlightSheet", async (_, { dispatch }) => {
  sheetThunkAction?.abort()
  sheetThunkAction = undefined

  dispatch(flightSearchSlice.actions.setFlightSheetOpen(false))
  dispatch(flightSearchSlice.actions.setSheetLoader(null))
  dispatch(flightSearchSlice.actions.setSearchLoading(false))
  dispatch(flightSearchSlice.actions.setPendingSpendingSheetInput(undefined))
  dispatch(flightSearchSlice.actions.setSource(undefined))
  dispatch(flightSearchSlice.actions.setFlightNotes(undefined))
  dispatch(flightSearchSlice.actions.setFlightPriceWatchQuoteRef(undefined))
  dispatch(flightSearchSlice.actions.setFlightPriceWatchQuotes([]))
  dispatch(flightSearchSlice.actions.setFlightSearchWatchQuote(undefined))
})

export default flightSearchSlice
